import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataFrame } from './data/DataFrame';
import { systemName } from './data/systems';
import {
  Badge,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Modal,
  Pagination,
  Select,
  SpaceBetween,
  Spinner,
  Table,
  Textarea,
  TextFilter,
} from '@cloudscape-design/components';
import {
  PageSkeleton,
  EmptyState,
  useAuth,
  useRoles,
  useNotify,
  createApiClient,
} from '@deloitte-us-consulting/gp-sdk';
import GrantFieldsEditor from './catalog/GrantFieldsEditor';
import { needsReason, type DataGrant, type LedgerEntry, type LedgerStatus, type SourceDetail } from './catalog/types';

interface AppRow {
  appId: string;
  displayName: string;
  enabled: boolean;
  externalUrl?: string;
  dataGrants?: DataGrant[];
}
interface SourceRow {
  sourceId: string;
  connector?: string;
  displayName: string;
  defaultClass: 'standard' | 'restricted';
}

const API_URL = (import.meta.env.VITE_API_URL as string) ?? '';
const PAGE_SIZE = 25;

const STATUS_FILTERS: { value: LedgerStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All outcomes' },
  { value: 'requested', label: 'Requested' },
  { value: 'granted', label: 'Granted' },
  { value: 'denied', label: 'Denied' },
  { value: 'revoked', label: 'Revoked' },
];

const statusBadge = (s: LedgerStatus) => {
  const color = { requested: 'blue', granted: 'green', denied: 'red', revoked: 'grey' } as const;
  return <Badge color={color[s]}>{s[0]!.toUpperCase() + s.slice(1)}</Badge>;
};

/** Ledger rows matching the text (app or source) and the outcome filter. */
export function filterLedger(entries: LedgerEntry[], text: string, status: LedgerStatus | 'all'): LedgerEntry[] {
  const q = text.trim().toLowerCase();
  return entries.filter(
    (e) => (status === 'all' || e.status === status) && (!q || e.appId.toLowerCase().includes(q) || e.sourceId.toLowerCase().includes(q) || (e.systemId ?? '').toLowerCase().includes(q)),
  );
}

interface Target {
  appId: string;
  sourceId: string;
}

/**
 * Who can read what, and the record of how they got it (ADR 0031). Shows each hosted
 * app's grants, lets an admin grant, change or revoke access, and lists the demand
 * ledger: every request and decision, whatever the outcome. Gated to platform admins;
 * the API enforces the same gate server-side.
 */
export default function DataAccessAdmin({ embedded = false }: { embedded?: boolean } = {}) {
  const { accessToken } = useAuth();
  const { can } = useRoles();
  const isAllowed = can('Data:Manage', { appId: '*' });
  const { notify } = useNotify();
  const api = useMemo(() => createApiClient(API_URL, () => accessToken), [accessToken]);

  const [apps, setApps] = useState<AppRow[] | null>(null);
  const [sources, setSources] = useState<SourceRow[]>([]);
  const [ledger, setLedger] = useState<LedgerEntry[]>([]);
  const [loading, setLoading] = useState(true);

  const [editing, setEditing] = useState<{ appId: string | null; sourceId: string | null } | null>(null);
  const [revoking, setRevoking] = useState<Target | null>(null);

  const [text, setText] = useState('');
  const [statusFilter, setStatusFilter] = useState<LedgerStatus | 'all'>('all');
  const [page, setPage] = useState(1);

  const reload = useCallback(async () => {
    const [a, s, l] = await Promise.all([
      api.get<AppRow[]>('/api/registry/all'),
      api.get<SourceRow[]>('/api/catalog/sources'),
      api.get<LedgerEntry[]>('/api/catalog/ledger'),
    ]);
    setApps(a);
    setSources(s);
    setLedger(l);
  }, [api]);

  useEffect(() => {
    if (!isAllowed) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    reload()
      .catch((e) => {
        if (!cancelled) notify({ type: 'error', header: 'Could not load data access', content: (e as Error).message });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Load once. Intentionally NOT depending on `notify` (a fresh function each render).
  }, [reload, isAllowed]);

  if (!isAllowed) {
    return (
      <DataFrame embedded={embedded} title="App access">
        <EmptyState title="Platform admin access required" description="Ask an admin to grant you the Admin role." />
      </DataFrame>
    );
  }
  if (loading || apps === null) {
    return (
      <DataFrame embedded={embedded} title="App access">
        <PageSkeleton label="Loading data access…" />
      </DataFrame>
    );
  }

  const sourceName = (id: string) => sources.find((s) => s.sourceId === id)?.displayName ?? id;
  const appName = (id: string) => apps.find((a) => a.appId === id)?.displayName ?? id;
  const rows = apps.flatMap((a) => (a.dataGrants ?? []).map((g) => ({ app: a, grant: g })));
  // The highest-level view: which systems each app touches. Tables and fields are the detail below.
  const sourceById = new Map(sources.map((s) => [s.sourceId, s]));
  const overview = apps
    .filter((a) => (a.dataGrants ?? []).length > 0)
    .map((a) => {
      const grants = a.dataGrants ?? [];
      return {
        app: a,
        systems: [...new Set(grants.map((g) => sourceById.get(g.sourceId)?.connector ?? 'airtable'))],
        tables: grants.length,
        restricted: grants.filter((g) => sourceById.get(g.sourceId)?.defaultClass === 'restricted').length,
      };
    });

  const visible = filterLedger(ledger, text, statusFilter);
  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const pageItems = visible.slice((Math.min(page, pageCount) - 1) * PAGE_SIZE, Math.min(page, pageCount) * PAGE_SIZE);

  return (
    <DataFrame embedded={embedded}
      title="App access"
      description="Which apps can read which data sources, and the record of every request and decision. Changes apply on the next request."
    >
      <SpaceBetween size="l">
        <Table
          variant="container"
          header={<Header variant="h2" counter={`(${overview.length})`} description="The systems each app reads from. Tables and fields are listed below.">Apps and their data systems</Header>}
          items={overview}
          trackBy={(r) => r.app.appId}
          columnDefinitions={[
            { id: 'app', header: 'App', cell: (r) => r.app.displayName, isRowHeader: true },
            { id: 'systems', header: 'Data systems', cell: (r) => <SpaceBetween direction="horizontal" size="xs">{r.systems.map((id) => <Badge key={id} color="blue">{systemName(id)}</Badge>)}</SpaceBetween> },
            { id: 'tables', header: 'Tables', cell: (r) => r.tables },
            { id: 'restricted', header: 'Restricted', cell: (r) => (r.restricted ? <Badge color="red">{r.restricted}</Badge> : '—') },
          ]}
          empty={<Box textAlign="center" color="text-body-secondary">No app has data access yet.</Box>}
        />
        <Table
          variant="container"
          header={
            <Header
              variant="h2"
              counter={`(${rows.length})`}
              actions={
                <Button variant="primary" onClick={() => setEditing({ appId: null, sourceId: null })}>
                  Grant access
                </Button>
              }
            >
              Current access
            </Header>
          }
          items={rows}
          trackBy={(r) => `${r.app.appId}/${r.grant.sourceId}`}
          empty={
            <Box textAlign="center" color="text-body-secondary">
              No app has data access yet.
            </Box>
          }
          columnDefinitions={[
            { id: 'app', header: 'App', cell: (r) => r.app.displayName, isRowHeader: true },
            { id: 'source', header: 'Source', cell: (r) => sourceName(r.grant.sourceId) },
            { id: 'fields', header: 'Fields', cell: (r) => r.grant.fieldIds.length },
            { id: 'live', header: 'App status', cell: (r) => (r.app.enabled ? 'Live' : 'Not live') },
            {
              id: 'actions',
              header: 'Actions',
              cell: (r) => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="inline-link" ariaLabel={`Edit ${r.app.displayName} ${sourceName(r.grant.sourceId)}`} onClick={() => setEditing({ appId: r.app.appId, sourceId: r.grant.sourceId })}>
                    Edit
                  </Button>
                  <Button variant="inline-link" ariaLabel={`Revoke ${r.app.displayName} ${sourceName(r.grant.sourceId)}`} onClick={() => setRevoking({ appId: r.app.appId, sourceId: r.grant.sourceId })}>
                    Revoke
                  </Button>
                </SpaceBetween>
              ),
            },
          ]}
        />

        <Table
          variant="container"
          header={
            <Header variant="h2" counter={`(${visible.length})`} description="Every request and decision, newest first.">
              Requests and decisions
            </Header>
          }
          items={pageItems}
          trackBy="id"
          filter={
            <SpaceBetween direction="horizontal" size="xs">
              <TextFilter
                filteringText={text}
                filteringPlaceholder="Find an app or source"
                filteringAriaLabel="Find an app or source"
                onChange={({ detail }) => {
                  setText(detail.filteringText);
                  setPage(1);
                }}
              />
              <Select
                selectedOption={STATUS_FILTERS.find((o) => o.value === statusFilter) ?? STATUS_FILTERS[0]!}
                options={STATUS_FILTERS}
                ariaLabel="Filter by outcome"
                onChange={({ detail }) => {
                  setStatusFilter((detail.selectedOption.value as LedgerStatus | 'all') ?? 'all');
                  setPage(1);
                }}
              />
            </SpaceBetween>
          }
          pagination={
            <Pagination
              currentPageIndex={Math.min(page, pageCount)}
              pagesCount={pageCount}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
            />
          }
          empty={
            <Box textAlign="center" color="text-body-secondary">
              Nothing recorded yet.
            </Box>
          }
          columnDefinitions={[
            { id: 'at', header: 'When', cell: (e) => new Date(e.at).toLocaleString() },
            { id: 'app', header: 'App', cell: (e) => appName(e.appId), isRowHeader: true },
            // A request for a whole system names no table yet; show it as the system.
            { id: 'source', header: 'Source', cell: (e) => (e.sourceId ? sourceName(e.sourceId) : `${systemName(e.systemId)} (whole system)`) },
            { id: 'status', header: 'Outcome', cell: (e) => statusBadge(e.status) },
            { id: 'fields', header: 'Fields', cell: (e) => (e.fieldIds.length ? e.fieldIds.length : '—') },
            { id: 'reason', header: 'Reason', cell: (e) => e.reason ?? '—', minWidth: 200 },
            { id: 'by', header: 'By', cell: (e) => e.actor },
          ]}
        />
      </SpaceBetween>

      {editing && (
        <GrantModal
          api={api}
          apps={apps.filter((a) => !a.externalUrl)}
          sources={sources}
          initial={editing}
          onDismiss={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void reload();
          }}
        />
      )}
      {revoking && (
        <RevokeModal
          api={api}
          target={revoking}
          appName={appName(revoking.appId)}
          sourceName={sourceName(revoking.sourceId)}
          onDismiss={() => setRevoking(null)}
          onSaved={() => {
            setRevoking(null);
            void reload();
          }}
        />
      )}
    </DataFrame>
  );
}

interface GrantModalProps {
  api: ReturnType<typeof createApiClient>;
  apps: AppRow[];
  sources: SourceRow[];
  initial: { appId: string | null; sourceId: string | null };
  onDismiss: () => void;
  onSaved: () => void;
}

function GrantModal({ api, apps, sources, initial, onDismiss, onSaved }: GrantModalProps) {
  const { notify } = useNotify();
  const [appId, setAppId] = useState(initial.appId);
  const [sourceId, setSourceId] = useState(initial.sourceId);
  const [detail, setDetail] = useState<SourceDetail | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!sourceId) {
      setDetail(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .get<SourceDetail>(`/api/catalog/sources/${sourceId}`)
      .then((d) => {
        if (cancelled) return;
        setDetail(d);
        const existing = apps.find((a) => a.appId === appId)?.dataGrants?.find((g) => g.sourceId === sourceId);
        setSelected(existing?.fieldIds ?? []);
        setReason('');
      })
      .catch((e) => notify({ type: 'error', header: 'Could not load the source', content: (e as Error).message }))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // Reload fields when the source or app changes; `notify` is a fresh function each render.
  }, [api, sourceId, appId]);

  const reasonMissing = detail !== null && needsReason(detail, selected) && !reason.trim();
  const canSave = !!appId && !!sourceId && detail !== null && selected.length > 0 && !reasonMissing;

  const save = async () => {
    if (!appId || !sourceId) return;
    setSaving(true);
    try {
      await api.put(`/api/catalog/apps/${appId}/grants/${sourceId}`, { fieldIds: selected, reason: reason.trim() || undefined });
      notify({ type: 'success', header: 'Access saved', content: `${appId} can read ${selected.length} field${selected.length === 1 ? '' : 's'} of ${detail?.displayName}.` });
      onSaved();
    } catch (e) {
      notify({ type: 'error', header: 'Could not save access', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const appOptions = apps.map((a) => ({ value: a.appId, label: a.displayName, description: a.appId }));
  const sourceOptions = sources.filter((s) => s.sourceId).map((s) => ({ value: s.sourceId, label: s.displayName }));
  const locked = initial.appId !== null;

  return (
    <Modal
      visible
      size="large"
      onDismiss={onDismiss}
      header={locked ? 'Edit access' : 'Grant access'}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} disabled={!canSave} onClick={save}>
              Save access
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="l">
        {!locked && (
          <>
            <FormField label="App" description="Hosted apps only: linked apps run outside the platform.">
              <Select
                selectedOption={appOptions.find((o) => o.value === appId) ?? null}
                options={appOptions}
                placeholder="Choose an app"
                filteringType="auto"
                ariaLabel="App"
                onChange={({ detail: d }) => setAppId(d.selectedOption.value ?? null)}
              />
            </FormField>
            <FormField label="Source">
              <Select
                selectedOption={sourceOptions.find((o) => o.value === sourceId) ?? null}
                options={sourceOptions}
                placeholder="Choose a source"
                ariaLabel="Source"
                onChange={({ detail: d }) => setSourceId(d.selectedOption.value ?? null)}
              />
            </FormField>
          </>
        )}
        {loading && <Spinner />}
        {detail && !loading && (
          <GrantFieldsEditor source={detail} selected={selected} onChange={setSelected} reason={reason} onReasonChange={setReason} />
        )}
        {locked && (
          <Container>
            <Box color="text-body-secondary">
              Saving replaces this app's fields for this source. To remove its access entirely, use Revoke.
            </Box>
          </Container>
        )}
      </SpaceBetween>
    </Modal>
  );
}

interface RevokeModalProps {
  api: ReturnType<typeof createApiClient>;
  target: Target;
  appName: string;
  sourceName: string;
  onDismiss: () => void;
  onSaved: () => void;
}

function RevokeModal({ api, target, appName, sourceName, onDismiss, onSaved }: RevokeModalProps) {
  const { notify } = useNotify();
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const revoke = async () => {
    setSaving(true);
    try {
      await api.put(`/api/catalog/apps/${target.appId}/grants/${target.sourceId}`, { fieldIds: [], reason: reason.trim() || undefined });
      notify({ type: 'success', header: 'Access revoked', content: `${appName} can no longer read ${sourceName}.` });
      onSaved();
    } catch (e) {
      notify({ type: 'error', header: 'Could not revoke access', content: (e as Error).message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      visible
      onDismiss={onDismiss}
      header="Revoke access"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onDismiss}>
              Cancel
            </Button>
            <Button variant="primary" loading={saving} onClick={revoke}>
              Revoke
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>
          {appName} will stop receiving {sourceName} on its next request.
        </Box>
        <FormField label="Reason (optional)">
          <Textarea value={reason} onChange={({ detail }) => setReason(detail.value)} rows={2} ariaLabel="Reason" />
        </FormField>
      </SpaceBetween>
    </Modal>
  );
}
